/**
 * CartLineItem — one cart row: thumbnail, name/SKU, promo or hint pill, stepper,
 * money column, remove.
 *
 * Ported from `tw-components.jsx` (grep `function CartLineItem`). Rendered in the
 * cart drawer with `className="cl-compact"`, whose density rules live in
 * `keyframes.js` (54px thumb, absolute hover-reveal Remove, 92px money track so the
 * line amounts align with the drawer footer's Subtotal).
 *
 * One deliberate divergence, the same one as `ProductCard`'s: a `media` node prop.
 * CLAUDE.md rule 2 forbids raw `<img>` in the app (no `onError` fallback), and
 * `design-system/` may not import from `common`, so the caller passes a rendered
 * element (`<ProductImage className="w-full h-full object-cover" />`). Upstream's
 * `image`-URL branch is kept as a fallback so the component still works standalone.
 *
 * Pills are either/or, promo winning: `promo` (green, `sell`) is a discount that is
 * already applied; `hint` (blue, `redeem`) is something the buyer may want to know
 * (e.g. "On auto-delivery"). `math` adds the `qty × unit price` line under the total.
 *
 * Second divergence: the `math` line formats with `PriceTag`'s `fmtPrice` recipe
 * (`toLocaleString`, thousands separators) instead of upstream's `.toFixed(2)` —
 * otherwise a $1,049.50 total sits beside a 1049.50 unit price in the same money
 * column. Same CLAUDE.md rule-1 reasoning as PriceTag's own header note.
 *
 * Third divergence: `unit` ("/ box") renders at the end of the `math` line, next to
 * the unit price. Upstream hands it to the total's PriceTag, which reads as
 * "$120.00 / box" for a 6-box line. Without `math` there is no unit price, so
 * `unit` isn't shown.
 */
import { cx } from "./cx";
import { Icon } from "./Icon.jsx";
import { PriceTag } from "./PriceTag.jsx";
import { QuantityStepper } from "./QuantityStepper.jsx";

function fmtPrice(n) {
  return Number(n).toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function CartLineItem({
  name,
  sku,
  media,
  image,
  icon = "medical_services",
  price,
  quantity = 1,
  unit,
  math = false,
  currency = "$",
  original,
  total,
  promo,
  hint,
  onQuantityChange,
  onRemove,
  style = {},
  className = "",
}) {
  return (
    <div
      className={cx(
        "dnd-cl flex gap-[var(--space-4)] py-[var(--space-4)] font-sans border-b border-line-subtle animate-[dnd-cl-in_var(--duration-base)_var(--ease-out)]",
        className
      )}
      style={style}
    >
      <div className="dnd-cl__thumb flex h-[76px] w-[76px] shrink-0 items-center justify-center overflow-hidden rounded-md border border-line-subtle bg-neutral-50">
        {media ||
          (image ? (
            <img
              className="h-full w-full object-cover"
              src={image}
              alt={name}
            />
          ) : (
            <Icon
              name={icon}
              size={34}
              weight={200}
              className="text-neutral-300"
            />
          ))}
      </div>
      <div className="dnd-cl__main flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="dnd-cl__name text-base font-semibold leading-[1.3] text-ink">
          {name}
        </span>
        {sku && <span className="dnd-cl__sku text-xs text-ink-3">{sku}</span>}
        {promo ? (
          <span className="dnd-cl__promo mt-[4px] inline-flex items-center gap-[5px] self-start rounded-[var(--radius-pill)] bg-success-50 py-[2px] pl-[6px] pr-[9px] text-2xs font-semibold text-success-700">
            <Icon name="sell" size={13} weight={500} fill />
            {promo}
          </span>
        ) : hint ? (
          <span className="dnd-cl__promo dnd-cl__promo--hint mt-[4px] inline-flex items-center gap-[5px] self-start rounded-[var(--radius-pill)] bg-[var(--surface-secondary-soft)] py-[2px] pl-[6px] pr-[9px] text-2xs font-semibold text-blue-700">
            <Icon name="redeem" size={13} weight={500} />
            {hint}
          </span>
        ) : null}
        <div className="dnd-cl__row mt-[var(--space-2)] flex items-center justify-between gap-[var(--space-3)]">
          <QuantityStepper value={quantity} onChange={onQuantityChange} />
          <div className="dnd-cl__money flex flex-col items-end gap-[2px]">
            <PriceTag
              amount={total !== undefined ? total : price * quantity}
              original={original}
              size="sm"
            />
            {math && (
              <span className="dnd-cl__math whitespace-nowrap text-2xs tabular-nums text-ink-2">
                {quantity} × {currency}
                {fmtPrice(price)}
                {unit && ` ${unit}`}
              </span>
            )}
          </div>
        </div>
        {onRemove && (
          <button
            onClick={onRemove}
            style={{ alignSelf: "flex-start", marginTop: 4 }}
            className="dnd-cl__remove inline-flex cursor-pointer items-center gap-[var(--space-1)] rounded-sm border-0 bg-transparent px-[6px] py-[var(--space-1)] font-sans text-xs text-ink-3 [transition:color_var(--duration-fast),background_var(--duration-fast)] hover:bg-error-50 hover:text-error-600"
          >
            <Icon name="delete" size={15} weight={500} />
            Remove
          </button>
        )}
      </div>
    </div>
  );
}
